/* GrowthOS — Email Templates list page (route /settings/templates).
   Rebuilt to the design's ListScreen (growthos/TemplateFlow.jsx → ListScreen +
   GridCard + Preview + ModuleModal + RowMenu): "Email Templates" head, toolbar
   (search + Module/Type filters + list/grid view toggle), a table with CSS email
   preview thumbnails + type/module chips, a grid-card view, client-side pagination,
   and a "New Template" module-picker modal. Wired to the real template store
   (getAllTemplate) + APIs: Edit → the existing updateTemplate builder route, Delete →
   apiServices.deleteTemplate, New Template → the existing gallery flow
   (/settings/templateGallery with the module). Shared design primitives (Ic, Preview,
   chips, avatar, resolveType) live in ./templatePreview. CSS is a scoped .gos-tpl block
   in the theme. Gallery (templateGallery.jsx) + unlayer Editor are separate pages. */
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import apiServices from '../../../services/service';
import { getAllTemplate } from '../../../redux/selector';
import { fetchDataAndUpdateStore } from '../../../redux/actions';
import { isLoadingByKey } from '../../../shared/controls/methods/getLoadingState';
import { Ic, Preview, TypeChip, ModChip, Avatar, fmtDate, modIcon, useOutside } from './templatePreview';

const RowMenu = ({ onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  return (
    <div className="gos-tpl-menuwrap" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button type="button" className={'gos-tpl-iconbtn' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
        <Ic name="dots" size={16} />
      </button>
      {open ? (
        <div className="gos-tpl-menu">
          <button type="button" className="gos-tpl-menu-item" onClick={() => { setOpen(false); onEdit(); }}><Ic name="edit" size={14} /> Edit</button>
          <button type="button" className="gos-tpl-menu-item danger sep" onClick={() => { setOpen(false); onDelete(); }}><Ic name="trash" size={14} /> Delete</button>
        </div>
      ) : null}
    </div>
  );
};

const MiniSelect = ({ label, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  return (
    <div className="gos-tpl-mini" ref={ref}>
      <button type="button" className={'gos-btn gos-btn-ghost gos-btn-sm' + (open ? ' on' : '') + (value !== 'All' ? ' has-count' : '')} onClick={() => setOpen((o) => !o)}>
        <span className="gos-tpl-mini-lbl">{label}: </span><strong>{value}</strong>
        <Ic name="chevD" size={14} />
      </button>
      {open ? (
        <div className="gos-tpl-mini-pop">
          {options.map((o) => (
            <button type="button" key={o} className="gos-tpl-mini-item" onClick={() => { onChange(o); setOpen(false); }}>
              <span className={'gos-tpl-check' + (o === value ? ' on' : '')}>{o === value ? <Ic name="check" size={11} /> : null}</span>
              {o}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ModuleModal = ({ open, onClose, onNext }) => {
  const [mod, setMod] = useState('');
  const [openSel, setOpenSel] = useState(false);
  const selRef = useOutside(openSel, () => setOpenSel(false));
  useEffect(() => { if (open) { setMod(''); setOpenSel(false); } }, [open]);
  const MODULES = ['Lead', 'Contact'];
  if (!open) return null;
  return (
    <div className="gos-tpl-ov" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="gos-tpl-modal">
        <div className="gos-tpl-modal-head">
          <div className="gos-tpl-modal-head-l">
            <span className="gos-tpl-modal-ic"><Ic name="mail" size={18} /></span>
            <div>
              <div className="gos-tpl-modal-title">New Email Template</div>
              <div className="gos-tpl-modal-sub">Choose which module this template will be used with.</div>
            </div>
          </div>
          <button type="button" className="gos-tpl-iconbtn" onClick={onClose}><Ic name="x" size={18} /></button>
        </div>
        <div className="gos-tpl-modal-body">
          <label className="gos-tpl-flabel">Associated module<span className="req"> *</span></label>
          <div className="gos-tpl-sel" ref={selRef}>
            <button type="button" className={'gos-tpl-sel-btn' + (openSel ? ' open' : '')} onClick={() => setOpenSel((o) => !o)}>
              <span className={mod ? '' : 'ph'}>{mod || 'Select a module'}</span>
              <Ic name="chevD" size={16} />
            </button>
            {openSel ? (
              <div className="gos-tpl-sel-menu">
                {MODULES.map((m) => (
                  <button type="button" key={m} className={'gos-tpl-sel-opt' + (m === mod ? ' on' : '')} onClick={() => { setMod(m); setOpenSel(false); }}>
                    <Ic name={modIcon(m)} size={15} /> {m}
                    {m === mod ? <Ic name="check" size={15} style={{ marginLeft: 'auto', color: 'var(--gos-accent)' }} /> : null}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <p className="gos-tpl-hint"><Ic name="info" size={14} /> Templates are scoped to a module so merge fields resolve correctly.</p>
        </div>
        <div className="gos-tpl-modal-foot">
          <span />
          <div className="gos-tpl-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" disabled={!mod} onClick={() => onNext(mod)}>Next <Ic name="arrowR" size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pager = ({ start, count, total, cur, totalPages, setPage }) => {
  const nums = [];
  for (let p = 1; p <= totalPages; p++) nums.push(p);
  return (
    <div className="gos-tpl-pager">
      <div className="gos-tpl-pager-info">Showing <strong>{start + 1}</strong>–<strong>{start + count}</strong> of <strong>{total}</strong></div>
      <div className="gos-tpl-pager-pages">
        <button type="button" className="gos-tpl-parrow" disabled={cur <= 1} onClick={() => setPage((p) => p - 1)}><Ic name="chevL" size={16} /></button>
        {nums.map((p) => (
          <button type="button" key={p} className={'gos-tpl-pnum' + (p === cur ? ' on' : '')} onClick={() => setPage(p)}>{p}</button>
        ))}
        <button type="button" className="gos-tpl-parrow" disabled={cur >= totalPages} onClick={() => setPage((p) => p + 1)}><Ic name="chevR" size={16} /></button>
      </div>
    </div>
  );
};

const GridCard = ({ t, onOpen, onDelete }) => (
  <div className="gos-tpl-gcard" onClick={() => onOpen(t)}>
    <div className="gos-tpl-gthumb">
      <Preview type={t.templateType} />
      <div className="gos-tpl-ghover">
        <button type="button" className="gos-btn gos-btn-primary gos-btn-sm" onClick={(e) => { e.stopPropagation(); onOpen(t); }}><Ic name="edit" size={14} /> Edit</button>
        <button type="button" className="gos-btn gos-btn-ghost gos-btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(t); }}><Ic name="trash" size={14} /></button>
      </div>
    </div>
    <div className="gos-tpl-gbody">
      <div className="gos-tpl-gtop"><TypeChip type={t.templateType} /></div>
      <div className="gos-tpl-gname">{t.name || 'Untitled template'}</div>
      <div className="gos-tpl-gsub">{t.subject || '—'}</div>
      <div className="gos-tpl-gfoot">
        <div className="gos-tpl-byline"><Avatar name={t.createdBy} size={20} /><span className="gos-tpl-by">{t.createdBy || '—'}</span></div>
        <ModChip mod={t.associatedModule} />
      </div>
    </div>
  </div>
);

const Templates = () => {
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'template');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const templateData = useSelector(getAllTemplate);

  const [rowData, setRowData] = useState(null);
  const [q, setQ] = useState('');
  const [view, setView] = useState('list');
  const [mod, setMod] = useState('All');
  const [type, setType] = useState('All');
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (templateData === null || templateData === undefined) {
      dispatch(fetchDataAndUpdateStore({ key: 'template' }));
    } else {
      setRowData(templateData);
    }
  }, [templateData, dispatch]);

  const rows = useMemo(() => rowData || [], [rowData]);
  const pageSize = view === 'list' ? 10 : 12;

  const modules = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.associatedModule).filter(Boolean)))], [rows]);
  const allTypes = useMemo(() => ['All', ...Array.from(new Set(rows.map((r) => r.templateType).filter(Boolean)))], [rows]);
  const moduleCount = Math.max(1, modules.length - 1);

  let visible = rows;
  if (q.trim()) {
    const s = q.toLowerCase();
    visible = visible.filter(
      (r) =>
        String(r.name || '').toLowerCase().includes(s) ||
        String(r.subject || '').toLowerCase().includes(s) ||
        String(r.createdBy || '').toLowerCase().includes(s)
    );
  }
  if (mod !== 'All') visible = visible.filter((r) => r.associatedModule === mod);
  if (type !== 'All') visible = visible.filter((r) => r.templateType === type);

  const total = visible.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => { setPage(1); }, [q, mod, type, view]);
  const cur = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (cur - 1) * pageSize;
  const pageRows = visible.slice(start, start + pageSize);

  const openEdit = (r) => {
    navigate('/settings/templateGallery/updateTemplate', {
      state: { module: r.associatedModule, data: { ...r, type: 'edit' } },
    });
  };

  const handleDelete = (r) => {
    if (!r?.id) return;
    apiServices
      .deleteTemplate({ id: [r.id] })
      .then((response) => {
        if (response?.status === 200) {
          toast.success('Template Deleted Successfully!', { position: 'top-right' });
          dispatch(fetchDataAndUpdateStore({ key: 'template' }));
        } else {
          toast.error('Error deleting template', { position: 'top-right' });
        }
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
        toast.error('Error deleting template', { position: 'top-right' });
      });
  };

  const onNextModule = (m) => {
    setModalOpen(false);
    navigate('/settings/templateGallery', { state: m });
  };

  const emptyMsg = storeLoading && rows.length === 0 ? 'Loading templates…' : 'No templates match your filters.';

  return (
    <div className="inner-page">
      <div className="gos-tpl">
        {/* head */}
        <div className="gos-tpl-head">
          <div>
            <div className="gos-tpl-h1">Email Templates</div>
            <div className="gos-tpl-sub">
              <strong>{rows.length}</strong> template{rows.length !== 1 ? 's' : ''} across <strong>{moduleCount}</strong> module{moduleCount !== 1 ? 's' : ''}
            </div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={() => setModalOpen(true)}>
            <Ic name="plus" size={16} /> New Template
          </button>
        </div>

        {/* toolbar */}
        <div className="gos-tpl-toolbar">
          <div className="gos-tpl-search">
            <Ic name="search" size={16} />
            <input placeholder="Search templates, subjects, authors…" value={q} onChange={(e) => setQ(e.target.value)} />
            {q ? <button type="button" className="gos-tpl-search-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
          </div>
          <div className="gos-tpl-grow" />
          <MiniSelect label="Module" value={mod} options={modules} onChange={setMod} />
          <MiniSelect label="Type" value={type} options={allTypes} onChange={setType} />
          <div className="gos-tpl-viewtoggle">
            <button type="button" className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}><Ic name="list" size={16} /> List</button>
            <button type="button" className={view === 'grid' ? 'on' : ''} onClick={() => setView('grid')}><Ic name="grid" size={15} /> Grid</button>
          </div>
        </div>

        {view === 'list' ? (
          <div className="gos-tpl-card">
            <div className="gos-tpl-tbl-scroll">
              <table className="gos-tpl-tbl">
                <thead>
                  <tr>
                    <th>Template Name</th>
                    <th>Subject</th>
                    <th>Module</th>
                    <th>Type</th>
                    <th>Created By</th>
                    <th>Updated</th>
                    <th style={{ width: 52 }} />
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => (
                    <tr key={r.id} onClick={() => openEdit(r)}>
                      <td>
                        <div className="gos-tpl-name-cell">
                          <span className="gos-tpl-thumb"><Preview type={r.templateType} compact /></span>
                          <div className="gos-tpl-name-meta">
                            <Link
                              to="/settings/templateGallery/updateTemplate"
                              state={{ module: r.associatedModule, data: { ...r, type: 'edit' } }}
                              className="gos-tpl-name-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {r.name || 'Untitled template'}
                            </Link>
                          </div>
                        </div>
                      </td>
                      <td><span className="gos-tpl-subject">{r.subject || '—'}</span></td>
                      <td><ModChip mod={r.associatedModule} /></td>
                      <td><TypeChip type={r.templateType} /></td>
                      <td><div className="gos-tpl-byline"><Avatar name={r.createdBy} size={24} /><span className="gos-tpl-by">{r.createdBy || '—'}</span></div></td>
                      <td><span className="gos-tpl-date">{fmtDate(r.updatedDateTime || r.createdDateTime || r.modifiedDate)}</span></td>
                      <td><RowMenu onEdit={() => openEdit(r)} onDelete={() => handleDelete(r)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {total === 0 ? <div className="gos-tpl-empty">{emptyMsg}</div> : null}
            </div>
            {total > 0 ? <Pager start={start} count={pageRows.length} total={total} cur={cur} totalPages={totalPages} setPage={setPage} /> : null}
          </div>
        ) : (
          <div>
            <div className="gos-tpl-grid">
              {pageRows.map((r) => (
                <GridCard key={r.id} t={r} onOpen={openEdit} onDelete={handleDelete} />
              ))}
            </div>
            {total === 0 ? <div className="gos-tpl-empty">{emptyMsg}</div> : null}
            {total > 0 ? (
              <div className="gos-tpl-card" style={{ marginTop: 16 }}>
                <Pager start={start} count={pageRows.length} total={total} cur={cur} totalPages={totalPages} setPage={setPage} />
              </div>
            ) : null}
          </div>
        )}
      </div>

      <ModuleModal open={modalOpen} onClose={() => setModalOpen(false)} onNext={onNextModule} />
    </div>
  );
};

export default Templates;
